// ---------------------------------------------------------------------------
// FC1 — contrato de erro interno do front (`.claude/rules/fc1-api-contract.md`,
// §A.5/§A.6). Dual da hierarquia `AppError` do backend.
//
// Todo erro de API chega à aplicação como uma instância desta classe, produzida
// pelo interceptor de contrato (`installContractInterceptor`). Componentes e
// services NUNCA leem `response.data.errors` à mão.
//
// ⚠️ Compatibilidade com o legado: além dos campos tipados, a instância
// RE-EXPÕE `response`, `config`, `request` e `isAxiosError` do erro original.
// Isso é deliberado — há ~50 páginas pré-FC1 cujo catch faz
// `get(err, 'response.data.errors', [...])`, e o interceptor é global. Sem esse
// passthrough, todas elas passariam a cair no fallback silenciosamente. O
// helper `isSessionDeadError` do saga de auth também depende disso.
// ---------------------------------------------------------------------------

// Discriminador de erro. Governa a política de feedback do FC4 (toast × inline).
export const API_ERROR_KINDS = Object.freeze({
   VALIDATION: 'validation',
   AUTH: 'auth',
   FORBIDDEN: 'forbidden',
   NOT_FOUND: 'not_found',
   CONFLICT: 'conflict',
   BUSINESS: 'business',
   SERVER: 'server',
   NETWORK: 'network',
});

// status 4xx → { kind, code } default. 422 é resolvido à parte (depende do
// `code` do backend: `business_rule` vira regra de negócio, o resto validação).
const CLIENT_STATUS_MAP = {
   400: { kind: API_ERROR_KINDS.VALIDATION, code: 'bad_request' },
   401: { kind: API_ERROR_KINDS.AUTH, code: 'unauthorized' },
   403: { kind: API_ERROR_KINDS.FORBIDDEN, code: 'forbidden' },
   404: { kind: API_ERROR_KINDS.NOT_FOUND, code: 'not_found' },
   409: { kind: API_ERROR_KINDS.CONFLICT, code: 'conflict' },
   // 413 morre no proxy (nginx), antes do Express: o corpo é HTML, não JSend.
   // Sem um code sintético o operador leria "Request failed with status code
   // 413" ao anexar arquivo grande demais.
   413: { kind: API_ERROR_KINDS.VALIDATION, code: 'payload_too_large' },
   429: { kind: API_ERROR_KINDS.BUSINESS, code: 'rate_limit' },
};

const GENERIC_MESSAGES = {
   network: 'Falha de conexão. Tente novamente.',
   server: 'Erro interno. Tente novamente.',
   unknown: 'Ocorreu um erro. Tente novamente.',
};

// Lê `errors[]` tolerando os três formatos que circulam hoje (FC1 §A.6.4):
//   1. JSend canônico  → [{ field, message, code }]
//   2. legado          → ['mensagem', 'outra']
//   3. ausente         → undefined
// NUNCA interpola objeto como string — é daqui que nascia o `[object Object]`.
//
// Além do mapa `field → message`, preserva cada violação-objeto COMPLETA em
// `items` (2026-09-10, handoff 0107): o backend passou a mandar campos
// estruturados por item de erro (`prod_id`, `shipped_qtd`, `current_version`,
// `changed_by`…) e este é o ÚNICO lugar que lê `errors[]` cru — o código de
// aplicação consome `apiError.errorItems`, nunca `response.data.errors`.
function parseErrorsList(rawErrors) {
   const list = Array.isArray(rawErrors) ? rawErrors : [];
   const fieldErrors = {};
   const messages = [];
   const items = [];
   let firstCode = null;

   list.forEach((entry) => {
      if (typeof entry === 'string') {
         if (entry) messages.push(entry);
         return;
      }
      if (!entry || typeof entry !== 'object') return;

      items.push({ ...entry });

      const message = typeof entry.message === 'string' ? entry.message : null;
      if (message) messages.push(message);
      if (!firstCode && typeof entry.code === 'string') firstCode = entry.code;
      // `field` pode ser null (erro cross-field) — só vira inline quem tem campo.
      if (entry.field && message) fieldErrors[entry.field] = message;
   });

   return { fieldErrors, messages, firstCode, items };
}

export default class ApiError extends Error {
   constructor({
      status,
      kind,
      code,
      message,
      fieldErrors = {},
      errorItems = [],
      errorId = null,
      retryable = false,
      raw = null,
   }) {
      super(message);
      this.name = 'ApiError';
      this.status = status;
      this.kind = kind;
      this.code = code;
      this.message = message;
      this.fieldErrors = fieldErrors;
      // Violações estruturadas de `errors[]`, uma por item (handoff 0107) —
      // carregam os campos extras do contrato (`prod_id`, `shipped_qtd`,
      // `current_version`, `changed_by`…) que o mapa `fieldErrors` não comporta.
      this.errorItems = errorItems;
      this.errorId = errorId;
      this.retryable = retryable;
      this.raw = raw;

      // ── passthrough do legado (ver cabeçalho) ────────────────────────────
      this.response = raw?.response;
      this.config = raw?.config;
      this.request = raw?.request;
      this.isAxiosError = raw?.isAxiosError ?? false;
   }

   // Açúcar para o código novo: `err.hasField('current_password')`.
   hasField(field) {
      return Boolean(this.fieldErrors[field]);
   }

   static fromAxiosError(error) {
      // Já normalizado (double-wrap defensivo) — devolve como está.
      if (error instanceof ApiError) return error;

      const response = error?.response;

      // 1. Sem response: rede, timeout, CORS, request abortado.
      if (!response) {
         return new ApiError({
            status: 0,
            kind: API_ERROR_KINDS.NETWORK,
            code: 'network_error',
            message: GENERIC_MESSAGES.network,
            retryable: true,
            raw: error,
         });
      }

      const status = response.status ?? 0;
      // 413 e erros de gateway podem vir com corpo HTML — só trata como JSend
      // se for objeto de verdade.
      const body =
         response.data && typeof response.data === 'object' ? response.data : {};
      const { fieldErrors, messages, firstCode, items } = parseErrorsList(
         body.errors
      );
      const bodyMessage =
         typeof body.message === 'string' && body.message ? body.message : null;

      // 2. 5xx — o `error_id` é o código de correlação de suporte (FC4 §B.7).
      if (status >= 500) {
         return new ApiError({
            status,
            kind: API_ERROR_KINDS.SERVER,
            code: firstCode ?? 'server_error',
            message: bodyMessage ?? GENERIC_MESSAGES.server,
            fieldErrors,
            errorItems: items,
            errorId: body.error_id ?? null,
            retryable: true,
            raw: error,
         });
      }

      // 3. 4xx — kind por status; 422 depende do code.
      let kind;
      let defaultCode;

      if (status === 422) {
         const isBusinessRule = firstCode === 'business_rule';
         kind = isBusinessRule
            ? API_ERROR_KINDS.BUSINESS
            : API_ERROR_KINDS.VALIDATION;
         defaultCode = isBusinessRule ? 'business_rule' : 'validation';
      } else {
         const mapped = CLIENT_STATUS_MAP[status];
         kind = mapped?.kind ?? API_ERROR_KINDS.VALIDATION;
         defaultCode = mapped?.code ?? 'bad_request';
      }

      const message =
         bodyMessage ??
         (messages.length ? messages.join('; ') : null) ??
         error?.message ??
         GENERIC_MESSAGES.unknown;

      return new ApiError({
         status,
         kind,
         code: firstCode ?? defaultCode,
         message,
         fieldErrors,
         errorItems: items,
         retryable: false,
         raw: error,
      });
   }
}
